import { csrfHeaders, forgetCsrfToken } from '@/lib/http';
import {
  parseExtractionResult,
  parseUploadError,
  type ExtractionResult,
  type Publisher,
  type UploadError,
} from './uploadTypes';

/**
 * Uploading a khutbah.
 *
 * <p>The editor surface is CSRF-protected and the token is issued lazily, so a
 * write has to fetch one first — the same exchange the browser does for every
 * other editor action. Doing it here rather than in the component keeps the
 * component unaware that CSRF exists at all.
 */

/** Thrown with the typed reason, so the caller can choose a severity. */
export class UploadFailed extends Error {
  readonly error: UploadError;

  constructor(error: UploadError) {
    super(error.message);
    this.name = 'UploadFailed';
    this.error = error;
  }
}

/**
 * @param replace the editor's explicit permission to supersede a khutbah this
 *                publisher already has for this Friday. Only ever sent after
 *                they have been shown what they are replacing and pressed the
 *                button — never retried automatically, and never a default.
 */
export async function uploadKhutbah(
  file: File,
  publisher: Publisher,
  friday: string,
  signal: AbortSignal,
  replace = false,
): Promise<ExtractionResult> {
  const csrf = await csrfHeaders(signal);

  const form = new FormData();
  form.append('file', file);
  form.append('publisher', publisher);
  if (friday) {
    form.append('friday', friday);
  }
  if (replace) {
    form.append('replace', 'true');
  }

  let response: Response;
  try {
    response = await fetch('/api/editor/uploads', {
      method: 'POST',
      body: form,
      signal,
      // Content-Type is deliberately unset: the browser must add the multipart
      // boundary itself, and setting it by hand produces a body the server
      // cannot parse.
      headers: { ...csrf, Accept: 'application/json' },
    });
  } catch (cause) {
    if (signal.aborted) {
      throw cause;
    }
    throw new UploadFailed({
      kind: 'network',
      message: 'The upload could not reach the server. Check the connection and try again.',
    });
  }

  if (!response.ok) {
    // A stale token survives a server restart; clearing it means the next
    // attempt fetches a fresh one rather than failing the same way forever.
    if (response.status === 401 || response.status === 403) {
      forgetCsrfToken();
    }
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // No body, or not JSON. The status is all there is.
    }
    throw new UploadFailed(parseUploadError(response.status, body));
  }

  return parseExtractionResult(await response.json());
}

